/**
 * URLs on ortex.news, ORTEX's news site.
 *
 * ortex.news owns ORTEX articles (owner decision 2026-10-19): the app's article
 * pages show the same content under the same id, so they canonicalize to it.
 * `slugify` is a port of ortex.news `src/lib/slugify.ts` + `plainHeadline` in
 * `src/lib/markdown.ts`; keep them in step. ortex.news redirects a wrong slug to
 * the right one, but a canonical should name the final URL, not a redirect.
 */

import { canonicalExchange } from "@/lib/stock-url";

export const ORTEX_NEWS_ORIGIN = "https://ortex.news";

const MD_LINK = /\[([^\]]+)\]\(([^)\s]+)\)/g;
const LEADING_HEADING = /^\s*#{1,6}\s+/;

/** Headline without markdown links or a leading heading marker (ortex.news `plainHeadline`). */
export const plainHeadline = (input: string | null | undefined): string => {
    if (!input) return "";
    return input
        .replace(LEADING_HEADING, "")
        .replace(MD_LINK, (_, text) => text as string)
        .trim();
};

/** ortex.news article slug for a headline. */
export const slugify = (input: string): string =>
    plainHeadline(input)
        .toLowerCase()
        .normalize("NFKD")
        .replace(/[̀-ͯ]/g, "")
        .replace(/[^a-z0-9\s-]/g, "")
        .trim()
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-")
        .slice(0, 100);

/** Canonical ortex.news URL for an article. */
export const ortexNewsArticleUrl = (id: number, headline: string): string => `${ORTEX_NEWS_ORIGIN}/articles/${id}/${slugify(headline)}`;

/**
 * Canonical ortex.news stock page: `/stock/<EXCHANGE>/<TICKER>`, upper-case,
 * which is the form ortex.news canonicalizes to. US venue aliases collapse first
 * (NasdaqGS -> NASDAQ), so one stock has one URL.
 */
export const ortexNewsStockUrl = (exchange: string, ticker: string): string =>
    `${ORTEX_NEWS_ORIGIN}/stock/${encodeURIComponent(canonicalExchange(exchange).toUpperCase())}/${encodeURIComponent(ticker.toUpperCase())}`;
